import type { IStructureStore, Component, CompProps, IPropsStore, Experiments } from '@wix/thunderbolt-symbols'
import { COMP_PATH_DELIMITER } from '@wix/thunderbolt-becky-types'
import { isExperimentOpen } from '@wix/thunderbolt-experiments'
import { hasTemplateContainer } from './repeaterUtils'

export const BUILDER_REPEATER_COMP_PATH_SPEC = 'specs.thunderbolt.builderRepeaterCompPath'

type TemplateCompPathComponent = Pick<Component, 'componentType' | 'slots'> & Partial<Pick<Component, 'components'>>
type TemplateCompPathStructure = Readonly<Record<string, TemplateCompPathComponent>>

const traversePageComponents = (
	structure: TemplateCompPathStructure,
	recursiveFunction: (compId: string, component: TemplateCompPathComponent) => void
): void => {
	Object.entries(structure)
		.filter(([_, component]) => component?.componentType === 'Page')
		.forEach(([compId, component]) => recursiveFunction(compId, component))
}

type TemplateCompPathOptions = {
	props?: IPropsStore
	experiments?: Experiments
}

type TemplateCompPathFromStructureOptions = {
	getCompProps?: (compId: string) => CompProps
	experiments?: Experiments
}

export const getCompIdToTemplateCompPath = (
	structureStore: IStructureStore,
	{ props, experiments }: TemplateCompPathOptions = {}
): Record<string, string> =>
	getCompIdToTemplateCompPathFromStructure(structureStore.getEntireStore(), {
		getCompProps: props && ((compId) => props.get(compId)),
		experiments,
	})

export const getCompIdToTemplateCompPathFromStructure = (
	structure: TemplateCompPathStructure,
	{ getCompProps, experiments }: TemplateCompPathFromStructureOptions = {}
): Record<string, string> => {
	const compIdToTemplateCompPath: Record<string, string> = {}

	// Builder repeaters carry `componentType: 'Builder'` and declare their repetition through a `template`
	// container in props, so the `componentType` check alone leaves their children without a `[]` segment.
	const shouldDetectBuilderRepeaters =
		!!getCompProps && !!experiments && isExperimentOpen(experiments, BUILDER_REPEATER_COMP_PATH_SPEC)

	const isRepeaterComp = (compId: string, component: TemplateCompPathComponent): boolean =>
		component?.componentType === 'Repeater' ||
		(shouldDetectBuilderRepeaters && hasTemplateContainer(getCompProps!(compId)))

	const getTemplateSlotChildIds = (compId: string, component: TemplateCompPathComponent): Set<string> => {
		const templateSlotChildIds = new Set<string>()
		if (!shouldDetectBuilderRepeaters) {
			return templateSlotChildIds
		}

		const compProps = getCompProps!(compId)
		const slots = component?.slots ?? {}
		const addReferencedSlotTargets = (value: unknown): void => {
			if (typeof value === 'string') {
				if (slots[value]) {
					templateSlotChildIds.add(slots[value])
				}
				return
			}
			if (Array.isArray(value)) {
				value.forEach(addReferencedSlotTargets)
				return
			}
			if (value && typeof value === 'object') {
				Object.values(value).forEach(addReferencedSlotTargets)
			}
		}

		Object.entries(compProps?.templateContainers ?? {}).forEach(([elementName, containers]) => {
			Object.keys(containers as Record<string, unknown>).forEach((containerName) => {
				if (elementName === 'root') {
					if (slots[containerName]) {
						templateSlotChildIds.add(slots[containerName])
					}
					addReferencedSlotTargets(compProps?.[containerName])
				}
			})
		})

		const addInnerTemplateTargets = (value: unknown): void => {
			if (!value || typeof value !== 'object') {
				return
			}
			if (!Array.isArray(value)) {
				const container = value as { containerType?: string; compId?: string }
				if (container.containerType === 'template' && typeof container.compId === 'string') {
					templateSlotChildIds.add(container.compId)
				}
			}
			Object.values(value).forEach(addInnerTemplateTargets)
		}
		addInnerTemplateTargets(compProps?.elementProps)

		return templateSlotChildIds
	}

	// visit all components recursively based on the structure and add their template comp path in the compIdToTemplateCompPath map
	// the template comp path looks like: comp-1/comp-2/comp-3
	// when we encounter a repeater we add [] to the path like so: comp-1/repeater-1/[]/comp-inside-repeater-item
	// in the *resolved* comp path inside the [] there will be the item id of the repeater item, so if a repeater has two items: item-1, item-2
	// the resolved comp paths will be: comp-1/repeater-1/[item-1]/comp-inside-repeater-item and comp-1/repeater-1/[item-2]/comp-inside-repeater-item
	const mapComponentToTemplatePath = (
		compId: string,
		component: TemplateCompPathComponent,
		accumulatedPath: string = ''
	): void => {
		const fullPath = `${accumulatedPath}${compId}`
		compIdToTemplateCompPath[compId] = fullPath

		const componentChildIds = component?.components ?? []
		const childPathPrefix = `${fullPath}${COMP_PATH_DELIMITER}`
		const childPrefixForRepeater = `${childPathPrefix}[]${COMP_PATH_DELIMITER}`
		const isRepeater = isRepeaterComp(compId, component)
		// The DS structure can hold a slotted child only in `slots`, while the renderer's comp path still passes through it.
		// Repeaters only follow their template slots, so the other slots are not given a [] segment.
		const slotChildIds = isRepeater
			? getTemplateSlotChildIds(compId, component)
			: new Set(Object.values(component?.slots ?? {}))
		const childComponentIds = Array.from(new Set([...componentChildIds, ...slotChildIds]))

		childComponentIds.forEach((childId) => {
			const newChildPathPrefix = isRepeater ? childPrefixForRepeater : childPathPrefix
			mapComponentToTemplatePath(childId, structure[childId], newChildPathPrefix)
		})
	}

	traversePageComponents(structure, mapComponentToTemplatePath)

	return compIdToTemplateCompPath
}
